import CancelIcon from '@mui/icons-material/Cancel';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import InfoIcon from '@mui/icons-material/Info';
import SegredoJustica from '@mui/icons-material/Lock';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Warning from '@mui/icons-material/Warning';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    ListSubheader,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import { grey, red } from '@mui/material/colors';
import SituacaoImpedimentosProcesso, { SITUACAO_IMPEDIMENTOS_PROCESSO } from '@/dominio/SituacaoImpedimentosProcesso';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import NewWindow from 'react-new-window';
import MeioJulgamentoProcesso from '../../dominio/MeioJulgamentoProcesso';
import { MODO_PARTICIPACAO_ADVOGADO } from '../../dominio/ModoParticipacaoAdvogado';
import LogoTST from '../../img/logo_tst.png';
import Configuracao from '../../util/Configuracao';
import NomeUtil from '../../util/NomeUtil';
import IconeOrigem from '../IconeOrigem';
import ReactComponent from '../ReactComponent';
import './../../css/listaPreferencias.css';
import IconeAdvogadoModoParticipacao from './icones/IconeAdvogadoModoParticipacao';

/**
 * Cor do segredo de justiça.
 * @readonly
 * @constant {string}
 */
const COR_SEGREDO_JUSTICA = red[900];

/**
 * Quebra de linha.
 * @readonly
 * @constant {string}
 */
const QUEBRA_LINHA_TXT = '\r\n';

/**
 * Indica ausência de informação.
 * @readonly
 * @constant {string}
 */
const SEM_INFORMACAO = '-';

class ModalListaPreferencias extends ReactComponent {
    /** @type {boolean} */
    openModalListaPreferencias = false;

    /** @type {string} */
    imprimir = '';

    /** @type {boolean} */
    mostrarSequencialOriginal = false;

    /** @type {boolean} */
    ocultarProcessosSemSequencial = false;

    /** @type {Array.<string | number>} */
    naoPreenchidos = [];

    /** @type {Array.<Array.<string | number>>} */
    duplicados = [];

    /** @type {boolean} */
    incluirPossiveisImpedimentos = true;

    /** @type {boolean} */
    incluirImpedimentosForaComposicao = true;

    /** @type {number} */
    totalDuplicados = 0;

    /** @type {Date | null} Data/hora da geração do arquivo-texto para compor o nome do arquivo. */
    dataHoraGeracaoTxt = null;

    constructor(props) {
        super(props);

        makeObservable(this, {
            openModalListaPreferencias: observable,
            imprimir: observable,
            mostrarSequencialOriginal: observable,
            ocultarProcessosSemSequencial: observable,
            naoPreenchidos: observable,
            duplicados: observable,
            incluirPossiveisImpedimentos: observable,
            incluirImpedimentosForaComposicao: observable,
            totalDuplicados: observable,
            dataHoraGeracaoTxt: observable,
            handleClose: action,
            abrirModalListaPreferencias: action,
            print: action,
            closePrint: action,
            mudarImprimir: action,
            handleClickExportarTxt: action,
            nomeArquivoTxt: computed,
            processosComInscricoesDeAdvogados: computed,
            existeProcessoComVistor: computed,
            existeProcessoComRevisor: computed,
            verificarPendenciasListaPreferencias: action,
            handleChangeMostrarSequencial: action,
            handleChangeOcultarProcessosSemSequencial: action,
            handleChangeIncluirPossiveisImpedimentos: action,
            handleChangeIncluirImpedimentosForaComposicao: action,
            habilitarOpcaoOcultarProcessosSemSequencial: computed,
            titulo: computed,
            subtitulo: computed,
            temProblema: computed,
            cabecalho: computed,
            exportacaoTxt: computed
        });
    }

    handleClose = () => {
        this.openModalListaPreferencias = false;
    };

    abrirModalListaPreferencias = () => {
        if (this.processosComInscricoesDeAdvogados.length > 0) {
            this.verificarPendenciasListaPreferencias();
            this.openModalListaPreferencias = true;
        } else {
            this.mensagemStore.abrirMensagemAlerta(
                'Não existem preferências cadastradas na sessão ou ocorreu um erro no carregamento (contate o administrador do sistema).'
            );
        }
    };

    print = () => {
        this.imprimir = 'none';
        setTimeout(() => {
            this.mudarImprimir();
        }, 2000);
    };

    closePrint = () => {
        this.mudarImprimir();
    };

    mudarImprimir() {
        this.imprimir = '';
    }

    handleClickExportarTxt = () => {
        this.dataHoraGeracaoTxt = new Date();
        const fileData = this.exportacaoTxt;
        const blob = new Blob([fileData], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = this.nomeArquivoTxt;
        link.href = url;
        link.click();
    };

    /**
     * Monta e retorna o nome do arquivo texto.
     *
     * @returns {string} O nome do arquivo texto.
     */
    get nomeArquivoTxt() {
        const nomeBase = 'lista_inscricoes_';
        const formatoDataHora = 'YYYY-MM-DD_HH[h]mm[min]ss[s]';
        const dataHoraGeracaoArquivoFormatada =
            this.dataHoraGeracaoTxt === null ? '' : moment(this.dataHoraGeracaoTxt).format(formatoDataHora);
        const extensaoArquivo = '.txt';
        return nomeBase + dataHoraGeracaoArquivoFormatada + extensaoArquivo;
    }

    get processosComInscricoesDeAdvogados() {
        return this.salaSessaoStore.processosSessaoOrdenadosPreferencias.filter(
            (registro) => registro?.pedidoSustentacaoOral && this.salaSessaoStore.temPartipacaoSessao(registro)
        );
    }

    get existeProcessoComVistor() {
        return (
            this.processosComInscricoesDeAdvogados.findIndex(
                (registro) =>
                    registro.informacoesAdicionais.RetornoVistaRegimental &&
                    registro.informacoesAdicionais.RetornoVistaRegimental.length > 0
            ) > -1
        );
    }

    get existeProcessoComRevisor() {
        return (
            this.processosComInscricoesDeAdvogados.findIndex(
                (registro) => registro.processo.revisor && registro.processo.revisor.sigla
            ) > -1
        );
    }

    /**
     * @desc Verifica duplicados e não preenchidos
     */
    verificarPendenciasListaPreferencias() {
        this.naoPreenchidos = [];
        this.duplicados = [];
        this.totalDuplicados = 0;

        this.processosComInscricoesDeAdvogados.forEach((registro) => {
            if (!registro.informacaoProcessoSalaSessao.sequenciaPreferencia) {
                this.naoPreenchidos.push(registro.processo.numeroCompleto);
            } else {
                const keySequencial = '' + registro.informacaoProcessoSalaSessao.sequenciaPreferencia;

                if (this.duplicados[keySequencial] === undefined) {
                    this.duplicados[keySequencial] = [];
                } else {
                    this.totalDuplicados++;
                }

                this.duplicados[keySequencial].push(registro.processo.numeroCompleto);
            }
        });
    }

    formataTipoJulgamento = (tipoJulgamento) => {
        return MeioJulgamentoProcesso.getDescricaoCurtaByCodigo(tipoJulgamento);
    };

    /**
     * Chave para identificar o registro do advogado unicamente.
     *
     * @param {Object} advogado O advogado
     * @returns {string} Chave única para o advogado.
     */
    getRepresentanteKey(advogado) {
        return `${advogado.regOAB.numero}${advogado.regOAB.letra ? '-' + advogado.regOAB.letra : ''}/${advogado.regOAB.siglaUf}`;
    }

    infoAdvogado = (advogado) => {
        let infoAdv = `${advogado.genero === 'F' ? 'Dra.' : 'Dr.'} ${advogado?.nome.trim()}`;

        if (advogado?.regOAB?.numero && advogado.regOAB.siglaUf) {
            infoAdv += ` - ${this.getRepresentanteKey(advogado)}`;
        }

        return infoAdv;
    };

    infoParticipacaoAdvogado = (advogado) => {
        if (advogado.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo) {
            return <Typography sx={{ fontStyle: 'italic', ml: 0.5 }}>(participação em sessão virtual)</Typography>;
        } else if (advogado.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo) {
            if (this.isComponenteHabilitado('IconeTelepresencialPreferencia')) {
                return (
                    <Box sx={{ ml: 1 }}>
                        <IconeAdvogadoModoParticipacao
                            style={{ maginTop: '0px' }}
                            modoParticipacao={advogado.modoParticipacao}
                        />
                    </Box>
                );
            } else {
                return (
                    <Typography sx={{ fontStyle: 'italic', ml: 0.5 }}>(participação por videoconferência)</Typography>
                );
            }
        }
    };

    renderAdvogados = (pedido) => {
        return pedido.advogados.map((advogado) => {
            return (
                <Box
                    key={this.getRepresentanteKey(advogado)}
                    sx={{ display: 'flex', flexDirection: 'row', marginTop: 1 }}
                >
                    <Typography
                        display={'inline'}
                        sx={{ ml: 2 }}
                    >
                        &bull;
                    </Typography>
                    <Typography
                        display={'inline'}
                        sx={{ ml: 1, fontWeight: 'bold' }}
                    >
                        {this.infoAdvogado(advogado)}
                    </Typography>
                    {this.infoParticipacaoAdvogado(advogado)}
                </Box>
            );
        });
    };

    revisorToString(registro) {
        if (!registro.processo?.revisor?.sigla) {
            return SEM_INFORMACAO;
        }

        return registro.processo.revisor.sigla;
    }

    retornoVistaRegimentalToString = (registro, caractereSeparador = ' ') => {
        if (
            !registro.informacoesAdicionais?.RetornoVistaRegimental ||
            registro.informacoesAdicionais.RetornoVistaRegimental.length === 0
        ) {
            return SEM_INFORMACAO;
        }

        return this.concatenarMagistrados(
            registro.informacoesAdicionais.RetornoVistaRegimental.map((vista) => {
                return vista.magistrado.sigla;
            }),
            caractereSeparador
        );
    };

    isImpedimentoComposicao(registro, impedimento) {
        return this.sessaoStore.isImpedimentoComposicao(registro, impedimento);
    }

    impedimentosToString = (registro, caractereSeparador = ' ') => {
        if (!registro.impedimentos || registro.impedimentos.length === 0) {
            return SEM_INFORMACAO;
        }

        const magistradosComImpedimentosConfirmados = registro.impedimentos.filter((impedimento) => {
            return (
                impedimento.situacaoImpedimento &&
                (impedimento.situacaoImpedimento === 'IMPEDIMENTO_DECLARADO' || this.incluirPossiveisImpedimentos) &&
                (this.incluirImpedimentosForaComposicao || this.isImpedimentoComposicao(registro, impedimento))
            );
        });

        if (magistradosComImpedimentosConfirmados.length === 0) {
            return SEM_INFORMACAO;
        }

        return this.concatenarMagistrados(
            magistradosComImpedimentosConfirmados.map((impedimento) => {
                return impedimento.magistrado.sigla;
            }),
            caractereSeparador
        );
    };

    concatenarMagistrados(magistradosArray, caractereSeparador) {
        const total = magistradosArray.length;
        let text = '';

        magistradosArray.forEach((element, i) => {
            text += element + (i < total - 1 ? caractereSeparador : '');
        });

        return text;
    }

    renderPartesEAdvogados(registro) {
        if (!registro?.pedidoSustentacaoOral?.pedidosDetalhados) {
            return null;
        }

        return registro.pedidoSustentacaoOral.pedidosDetalhados.map((pedido) => {
            return (
                <Box
                    sx={{ mt: 1 }}
                    key={this.nextKey}
                >
                    <Typography
                        display={'inline'}
                        sx={{ fontStyle: 'italic' }}
                    >
                        {pedido.denominacaoDaParte ? pedido.denominacaoDaParte.trim() + ': ' : ''}
                    </Typography>
                    <Typography display={'inline'}>
                        {registro.segredoJustica
                            ? NomeUtil.abreviarNome(pedido.nomeDaParte.trim())
                            : pedido.nomeDaParte.trim()}
                    </Typography>
                    {this.renderAdvogados(pedido)}
                </Box>
            );
        });
    }

    handleChangeMostrarSequencial = () => {
        this.mostrarSequencialOriginal = !this.mostrarSequencialOriginal;
    };

    handleChangeOcultarProcessosSemSequencial = () => {
        this.ocultarProcessosSemSequencial = !this.ocultarProcessosSemSequencial;
    };

    handleChangeIncluirPossiveisImpedimentos = () => {
        this.incluirPossiveisImpedimentos = !this.incluirPossiveisImpedimentos;
    };

    handleChangeIncluirImpedimentosForaComposicao = () => {
        this.incluirImpedimentosForaComposicao = !this.incluirImpedimentosForaComposicao;
    };

    get habilitarOpcaoOcultarProcessosSemSequencial() {
        return this.naoPreenchidos && this.naoPreenchidos.length > 0;
    }

    renderOpcoes() {
        return (
            <List
                dense
                sx={{ width: '100%', maxWidth: 424 }}
                subheader={<ListSubheader>Configurações</ListSubheader>}
            >
                <ListItem>
                    <ListItemIcon>
                        <FormatListNumberedIcon />
                    </ListItemIcon>
                    <ListItemText
                        id="switch-list-label-mostrar-sequencial-original"
                        primary="Mostrar sequencial original"
                    />
                    <Switch
                        size="small"
                        checked={this.mostrarSequencialOriginal}
                        onChange={this.handleChangeMostrarSequencial}
                        color="primary"
                        name="Mostrar sequencial original"
                        slotProps={{
                            input: {
                                'aria-labelledby': 'switch-list-label-mostrar-sequencial-original'
                            }
                        }}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <VisibilityOffIcon />
                    </ListItemIcon>
                    <ListItemText
                        id="switch-list-label-ocultar-processos-sem-sequencial"
                        primary="Ocultar processos sem sequencial"
                    />
                    <Tooltip
                        title={
                            this.habilitarOpcaoOcultarProcessosSemSequencial ? (
                                <Typography>
                                    Há {this.naoPreenchidos.length} sequencia
                                    {this.naoPreenchidos.length === 1 ? 'l' : 'is'} não preenchido
                                    {this.naoPreenchidos.length === 1 ? '' : 's'}.
                                </Typography>
                            ) : (
                                <>
                                    <Typography display="inline">Todos os sequenciais estão preenchidos.</Typography>
                                    <DoneAllIcon
                                        style={{ marginRight: '4px', verticalAlign: 'middle', fill: 'lightgreen' }}
                                    />
                                </>
                            )
                        }
                    >
                        <InfoIcon
                            fontSize="small"
                            style={{ marginRight: '4px', fill: grey[600] }}
                        />
                    </Tooltip>
                    <Switch
                        size="small"
                        checked={this.ocultarProcessosSemSequencial}
                        onChange={this.handleChangeOcultarProcessosSemSequencial}
                        color="primary"
                        name="Ocultar processos sem sequencial"
                        slotProps={{
                            input: {
                                'aria-labelledby': 'switch-list-label-ocultar-processos-sem-sequencial'
                            }
                        }}
                        disabled={!this.habilitarOpcaoOcultarProcessosSemSequencial}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <GroupRemoveIcon />
                    </ListItemIcon>
                    <ListItemText
                        id="switch-list-label-incluir-impedimentos-fora-composicao"
                        primary="Incluir impedimentos fora da composição"
                    />
                    <Switch
                        size="small"
                        checked={this.incluirImpedimentosForaComposicao}
                        onChange={this.handleChangeIncluirImpedimentosForaComposicao}
                        color="primary"
                        name="Incluir impedimentos fora da composição"
                        slotProps={{
                            input: {
                                'aria-labelledby': 'switch-list-label-incluir-impedimentos-fora-composicao'
                            }
                        }}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        {SituacaoImpedimentosProcesso.getIcone(
                            this.incluirImpedimentosForaComposicao
                                ? SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_FORA_DA_COMPOSICAO.id
                                : SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_NA_COMPOSICAO.id
                        )}
                    </ListItemIcon>
                    <ListItemText
                        id="switch-list-label-incluir-possiveis-impedimentos"
                        primary="Incluir possíveis impedimentos"
                    />
                    <Switch
                        size="small"
                        checked={this.incluirPossiveisImpedimentos}
                        onChange={this.handleChangeIncluirPossiveisImpedimentos}
                        color="primary"
                        name="Incluir possíveis impedimentos"
                        slotProps={{
                            input: {
                                'aria-labelledby': 'switch-list-label-incluir-possiveis-impedimentos'
                            }
                        }}
                    />
                </ListItem>
            </List>
        );
    }

    ocultarLinha(registro) {
        return !!(
            this.ocultarProcessosSemSequencial &&
            (!registro?.informacaoProcessoSalaSessao?.sequenciaPreferencia ||
                Number.parseInt(registro.informacaoProcessoSalaSessao.sequenciaPreferencia, 10) <= 0)
        );
    }

    get titulo() {
        let titulo = 'Lista de Preferências';

        if (this.sessaoStore.sessaoSelecionada) {
            titulo += ' - ';
            titulo += this.sessaoStore.sessaoSelecionada.tipo === 'O' ? 'Sessão Ordinária' : 'Sessão Extraordinária';
        }

        titulo += ' - ';
        titulo += this.sessaoStore.sessaoSelecionada.orgaoJudicante.descricao;

        return titulo;
    }

    get subtitulo() {
        let subtitulo = moment(
            this.sessaoStore.sessaoSelecionada.prazos.dataInicioSessaoPresencial,
            'DD-MM-YYYY-HH-mm-ss'
        ).format('DD/MM/YYYY HH:mm');
        subtitulo += ' - ';
        subtitulo += this.sessaoStore.sessaoSelecionada.descricao;

        return subtitulo;
    }

    get temProblema() {
        return this.naoPreenchidos.length > 0 || this.totalDuplicados > 0;
    }

    get cabecalho() {
        return (
            <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ justifyContent: 'center' }}>
                    <img
                        src={LogoTST}
                        height={60}
                        width={245}
                        style={{ marginLeft: '16px' }}
                        alt="Justiça do Trabalho"
                    />
                </Box>
                <Box sx={{ pb: 1.5, ml: 3 }}>
                    <Typography sx={{ justifyContent: 'center', fontWeight: 'bold' }}>{this.titulo}</Typography>
                    <Typography sx={{ justifyContent: 'center', fontWeight: 'bold' }}>{this.subtitulo}</Typography>
                </Box>
            </Box>
        );
    }

    get exportacaoTxt() {
        if (!this.processosComInscricoesDeAdvogados || this.processosComInscricoesDeAdvogados.length === 0) {
            return '';
        }

        const caractereSeparadorMagistrador = ';';

        const textosArray = this.processosComInscricoesDeAdvogados.map((registro) => {
            let text = '';

            text += registro.informacaoProcessoSalaSessao?.sequenciaPreferencia
                ? String(registro.informacaoProcessoSalaSessao.sequenciaPreferencia).padStart(2, '0')
                : '??';
            text += ') ';

            text += registro.processo?.numeroCompleto
                ? registro.processo.numeroCompleto
                : 'NÚMERO DO PROCESSO NÃO INFORMADO';

            text += ' (';
            text += `Relator: ${registro.processo?.relator?.sigla ? registro.processo.relator.sigla : 'NÃO INFORMADO'}`;

            const textRevisor = this.revisorToString(registro);
            if (textRevisor !== SEM_INFORMACAO) {
                text += ` / Revisor: ${textRevisor}`;
            }

            const textVR = this.retornoVistaRegimentalToString(registro, caractereSeparadorMagistrador);
            if (textVR !== SEM_INFORMACAO) {
                text += ` / Vistor${textVR.indexOf(caractereSeparadorMagistrador) !== -1 ? 'es' : ''}: ${textVR}`;
            }

            const textImpedimentos = this.impedimentosToString(registro, caractereSeparadorMagistrador);
            if (textImpedimentos !== SEM_INFORMACAO) {
                text += ` / Impedido${textImpedimentos.indexOf(caractereSeparadorMagistrador) !== -1 ? 's' : ''}: ${textImpedimentos}`;
            }

            text += ')';

            text += registro.segredoJustica ? ' [SEGREDO DE JUSTIÇA]' : '';
            text += this.montarTxtPartesEAdvogados(registro);
            return text;
        });

        let exportacao = '';
        textosArray.forEach((element) => {
            exportacao += element;
            exportacao += QUEBRA_LINHA_TXT;
            exportacao += QUEBRA_LINHA_TXT;
        });

        return exportacao;
    }

    montarTxtPartesEAdvogados(registro) {
        let text = '';

        const inscricoesArray = registro.pedidoSustentacaoOral.pedidosDetalhados.map((pedido) => {
            let textInscricoes = '';
            textInscricoes += pedido.denominacaoDaParte ? pedido.denominacaoDaParte + ': ' : '';
            textInscricoes += registro.segredoJustica ? NomeUtil.abreviarNome(pedido.nomeDaParte) : pedido.nomeDaParte;
            textInscricoes += this.montarTxtAdvogados(pedido);
            return textInscricoes;
        });

        inscricoesArray.forEach((element) => {
            text += QUEBRA_LINHA_TXT + element;
        });

        return text;
    }

    montarTxtAdvogados(pedido) {
        let text = '';

        const advogadosArray = pedido.advogados.map((advogado) => {
            return this.infoAdvogado(advogado) + this.infoParticipacaoAdvogadoTxt(advogado);
        });

        advogadosArray.forEach((element) => {
            text += QUEBRA_LINHA_TXT + element;
        });

        return text;
    }

    infoParticipacaoAdvogadoTxt(advogado) {
        if (advogado?.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo) {
            return ' (participação em sessão virtual)';
        } else if (advogado?.modoParticipacao === MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo) {
            return ' (participação por videoconferência)';
        }

        return '';
    }

    renderTableHead(versaoParaImpressao = false) {
        return (
            <TableHead>
                <TableRow>
                    <TableCell align="center">
                        <Typography fontWeight={'bold'}>Seq.</Typography>
                    </TableCell>
                    {!versaoParaImpressao && <TableCell></TableCell>}
                    <TableCell
                        style={{ minWidth: '320px' }}
                        align="left"
                    >
                        <Typography fontWeight={'bold'}>Processo / Partes e Advogados</Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography fontWeight={'bold'}>Relator</Typography>
                    </TableCell>
                    {this.existeProcessoComVistor && (
                        <TableCell align="center">
                            <Typography fontWeight={'bold'}>Vistor(es)</Typography>
                        </TableCell>
                    )}
                    {this.existeProcessoComRevisor && (
                        <TableCell align="center">
                            <Typography fontWeight={'bold'}>Revisor</Typography>
                        </TableCell>
                    )}
                    <TableCell align="center">
                        <Typography fontWeight={'bold'}>Impedimento(s)</Typography>
                    </TableCell>
                </TableRow>
            </TableHead>
        );
    }

    renderNumeroProcesso(registro) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                }}
            >
                <Typography
                    display={'inline'}
                    sx={{ fontWeight: 'bold' }}
                >
                    {registro.processo.numeroCompleto}
                </Typography>
                {this.parametroStore.isOrgaoTST && registro.processo?.origem === Configuracao.ORIGEM_PJE && (
                    <Box sx={{ ml: 1 }}>
                        <IconeOrigem processo={registro.processo} />
                    </Box>
                )}
                {registro.segredoJustica && (
                    <Box sx={{ ml: 1 }}>
                        <Tooltip title={<Typography>SEGREDO DE JUSTIÇA</Typography>}>
                            <SegredoJustica />
                        </Tooltip>
                    </Box>
                )}
            </Box>
        );
    }

    render() {
        return (
            <>
                <Tooltip
                    title={<Typography>Visualizar/Imprimir lista de preferências</Typography>}
                    placement="top-start"
                >
                    <Box
                        sx={{ display: 'flex', margin: 1, cursor: 'pointer', fill: 'white' }}
                        className="corBotaoAmarelo"
                        onClick={this.abrirModalListaPreferencias}
                    >
                        <PrintIcon />
                    </Box>
                </Tooltip>
                {this.openModalListaPreferencias && (
                    <Dialog
                        open={this.openModalListaPreferencias}
                        onClose={this.handleClose}
                        maxWidth="xl"
                    >
                        <DialogTitle>
                            {this.cabecalho}
                            {this.temProblema && (
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 1,
                                        mb: 1,
                                        maxWidth: '45%',
                                        fontSize: 'small'
                                    }}
                                >
                                    <Box sx={{ color: 'red', display: 'flex', alignItens: 'center' }}>
                                        <Typography>Problemas identificados</Typography>
                                        <Tooltip
                                            title={
                                                <Typography>
                                                    Para a correta impressão da lista de preferências, todos os
                                                    sequenciais de inscrições de advogados devem estar preenchidos e
                                                    serem únicos. Corrija-os manualmente ou, automaticamente, por meio
                                                    da funcionalidade de Ordenação para imprimir a lista de preferências
                                                    adequadamente.
                                                </Typography>
                                            }
                                            placement="left-start"
                                            style={{
                                                textAlign: 'justify',
                                                textJustify: 'inter-word'
                                            }}
                                        >
                                            <InfoIcon sx={{ ml: 1, mt: 0.3, fontSize: 'large' }} />
                                        </Tooltip>
                                    </Box>
                                    {this.naoPreenchidos.length > 0 && (
                                        <Typography fontSize={'small'}>
                                            {this.naoPreenchidos.length} processo
                                            {this.naoPreenchidos.length === 1 ? '' : 's'} com sequencia
                                            {this.naoPreenchidos.length === 1 ? 'l' : 'is'} não preenchido
                                            {this.naoPreenchidos.length === 1 ? '' : 's'}
                                        </Typography>
                                    )}
                                    {this.totalDuplicados > 0 && (
                                        <Typography fontSize={'small'}>
                                            {this.totalDuplicados} processo
                                            {this.totalDuplicados === 1 ? '' : 's'} com sequencia
                                            {this.totalDuplicados === 1 ? 'l' : 'is'} duplicado
                                            {this.totalDuplicados === 1 ? '' : 's'}
                                        </Typography>
                                    )}
                                </Paper>
                            )}
                            <Divider />
                            {this.renderOpcoes()}
                            <Divider />
                        </DialogTitle>
                        <DialogContent>
                            <Table stickyHeader>
                                {this.renderTableHead()}
                                <TableBody>
                                    {this.processosComInscricoesDeAdvogados.length > 0 ? (
                                        this.processosComInscricoesDeAdvogados.map((registro, i) => {
                                            return (
                                                !this.ocultarLinha(registro) && (
                                                    <TableRow
                                                        style={{ backgroundColor: i % 2 === 0 ? 'lightBlue' : '' }}
                                                        key={registro.processo.numeroCompleto}
                                                    >
                                                        <TableCell
                                                            style={{
                                                                color: registro.segredoJustica
                                                                    ? COR_SEGREDO_JUSTICA
                                                                    : ''
                                                            }}
                                                        >
                                                            <Box sx={{ fontWeight: 'bold', ml: 0.5 }}>
                                                                {registro.informacaoProcessoSalaSessao
                                                                    .sequenciaPreferencia ? (
                                                                    <Typography fontWeight={'bold'}>
                                                                        {
                                                                            registro.informacaoProcessoSalaSessao
                                                                                .sequenciaPreferencia
                                                                        }
                                                                    </Typography>
                                                                ) : (
                                                                    <Tooltip
                                                                        placement="right-start"
                                                                        title={
                                                                            <Typography>
                                                                                Sequencial de preferência não definido
                                                                            </Typography>
                                                                        }
                                                                    >
                                                                        <Warning
                                                                            style={{
                                                                                fill: 'darkorange',
                                                                                padding: '0px 0px',
                                                                                marginLeft: '0px',
                                                                                cursor: 'default'
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                            {registro.pedidoSustentacaoOral?.sequenciaDigitacao ? ( // Processo eSIJ
                                                                <Typography
                                                                    style={{ fontStyle: 'italic', color: 'grey' }}
                                                                >
                                                                    {this.mostrarSequencialOriginal &&
                                                                        ' (' +
                                                                            registro.pedidoSustentacaoOral
                                                                                .sequenciaDigitacao +
                                                                            ')'}
                                                                </Typography>
                                                            ) : (
                                                                registro.informacoesAdicionais &&
                                                                registro.informacoesAdicionais.NumeroPauta !==
                                                                    undefined && ( // Processo PJe
                                                                    <Typography
                                                                        style={{ fontStyle: 'italic', color: 'grey' }}
                                                                    >
                                                                        {this.mostrarSequencialOriginal &&
                                                                            ' (' +
                                                                                registro.informacoesAdicionais
                                                                                    .NumeroPauta +
                                                                                ')'}
                                                                    </Typography>
                                                                )
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {registro.informacaoProcessoSalaSessao
                                                                .sequenciaPreferencia &&
                                                                this.duplicados[
                                                                    registro.informacaoProcessoSalaSessao
                                                                        .sequenciaPreferencia
                                                                ] &&
                                                                this.duplicados[
                                                                    registro.informacaoProcessoSalaSessao
                                                                        .sequenciaPreferencia
                                                                ].length > 1 && (
                                                                    <Tooltip
                                                                        title={
                                                                            <Typography>
                                                                                Sequencial de preferência repetido
                                                                            </Typography>
                                                                        }
                                                                    >
                                                                        <Warning
                                                                            style={{
                                                                                fill: 'darkorange',
                                                                                padding: '0px 0px',
                                                                                marginLeft: '0px',
                                                                                cursor: 'default'
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                textAlign: 'left',
                                                                minWidth: '320px',
                                                                color: registro.segredoJustica
                                                                    ? COR_SEGREDO_JUSTICA
                                                                    : ''
                                                            }}
                                                        >
                                                            {this.renderNumeroProcesso(registro)}
                                                            {this.renderPartesEAdvogados(registro)}
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                textAlign: 'center',
                                                                color: registro.segredoJustica
                                                                    ? COR_SEGREDO_JUSTICA
                                                                    : ''
                                                            }}
                                                        >
                                                            <Typography>
                                                                {registro.processo.relator
                                                                    ? registro.processo.relator.sigla
                                                                    : SEM_INFORMACAO}
                                                            </Typography>
                                                        </TableCell>
                                                        {this.existeProcessoComVistor && (
                                                            <TableCell
                                                                style={{
                                                                    textAlign: 'center',
                                                                    color: registro.segredoJustica
                                                                        ? COR_SEGREDO_JUSTICA
                                                                        : ''
                                                                }}
                                                            >
                                                                <Typography>
                                                                    {this.retornoVistaRegimentalToString(registro)}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        {this.existeProcessoComRevisor && (
                                                            <TableCell
                                                                style={{
                                                                    textAlign: 'center',
                                                                    color: registro.segredoJustica
                                                                        ? COR_SEGREDO_JUSTICA
                                                                        : ''
                                                                }}
                                                            >
                                                                <Typography>
                                                                    {this.revisorToString(registro)}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        <TableCell
                                                            style={{
                                                                textAlign: 'center',
                                                                color: registro.segredoJustica
                                                                    ? COR_SEGREDO_JUSTICA
                                                                    : ''
                                                            }}
                                                        >
                                                            <Typography>
                                                                {this.impedimentosToString(registro)}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            );
                                        })
                                    ) : (
                                        <Typography sx={{ mt: 4, mb: 4, color: 'red' }}>
                                            Não existem preferências cadastradas.
                                        </Typography>
                                    )}
                                </TableBody>
                            </Table>
                            <Divider />
                        </DialogContent>
                        <div style={{ display: this.imprimir }}>
                            <DialogActions sx={{ mt: 1.5, mb: 1, mr: 4 }}>
                                <Button
                                    onClick={this.handleClose}
                                    color="secondary"
                                    variant="outlined"
                                    endIcon={<CancelIcon />}
                                    sx={{ mr: 2 }}
                                >
                                    Fechar
                                </Button>
                                <Button
                                    onClick={this.handleClickExportarTxt}
                                    color="primary"
                                    variant="outlined"
                                    endIcon={<DownloadForOfflineIcon />}
                                >
                                    Exportar
                                </Button>
                                <Button
                                    onClick={this.print}
                                    color="primary"
                                    variant="contained"
                                    endIcon={<PrintIcon />}
                                    sx={{ ml: 2 }}
                                >
                                    Imprimir
                                </Button>
                            </DialogActions>
                        </div>
                    </Dialog>
                )}
                {this.imprimir === 'none' && (
                    <NewWindow
                        onOpen={(win) =>
                            setTimeout(() => {
                                win.print();
                            }, 1000)
                        }
                        title={(this.titulo + ' - ' + this.subtitulo).replaceAll(' ', '_')}
                    >
                        {this.cabecalho}
                        <Table stickyHeader>
                            {this.renderTableHead(true)}
                            <TableBody>
                                {this.processosComInscricoesDeAdvogados.length > 0 ? (
                                    this.processosComInscricoesDeAdvogados.map((registro, i) => {
                                        return (
                                            !this.ocultarLinha(registro) && (
                                                <TableRow
                                                    style={{ backgroundColor: i % 2 === 0 ? 'lightBlue' : '' }}
                                                    className="unbreakableBlock"
                                                    key={this.nextKey}
                                                >
                                                    <TableCell
                                                        style={{
                                                            color: registro.segredoJustica ? COR_SEGREDO_JUSTICA : ''
                                                        }}
                                                    >
                                                        <Box sx={{ fontWeight: 'bold', marginLeft: 0.5 }}>
                                                            <Typography>
                                                                {
                                                                    registro.informacaoProcessoSalaSessao
                                                                        .sequenciaPreferencia
                                                                }
                                                            </Typography>
                                                        </Box>
                                                        {registro?.pedidoSustentacaoOral?.sequenciaDigitacao ? ( // Processo eSIJ
                                                            <Typography style={{ fontStyle: 'italic', color: 'grey' }}>
                                                                {this.mostrarSequencialOriginal &&
                                                                    ' (' +
                                                                        registro.pedidoSustentacaoOral
                                                                            .sequenciaDigitacao +
                                                                        ')'}
                                                            </Typography>
                                                        ) : (
                                                            registro.informacoesAdicionais &&
                                                            registro.informacoesAdicionais.NumeroPauta !==
                                                                undefined && ( // Processo PJe
                                                                <Typography
                                                                    style={{ fontStyle: 'italic', color: 'grey' }}
                                                                >
                                                                    {this.mostrarSequencialOriginal &&
                                                                        ' (' +
                                                                            registro.informacoesAdicionais.NumeroPauta +
                                                                            ')'}
                                                                </Typography>
                                                            )
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            textAlign: 'left',
                                                            minWidth: '320px',
                                                            color: registro.segredoJustica ? COR_SEGREDO_JUSTICA : ''
                                                        }}
                                                    >
                                                        {this.renderNumeroProcesso(registro)}
                                                        {this.renderPartesEAdvogados(registro)}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            textAlign: 'center',
                                                            color: registro.segredoJustica ? COR_SEGREDO_JUSTICA : ''
                                                        }}
                                                    >
                                                        <Typography>{registro.processo.relator.sigla}</Typography>
                                                    </TableCell>
                                                    {this.existeProcessoComVistor && (
                                                        <TableCell
                                                            style={{
                                                                textAlign: 'center',
                                                                color: registro.segredoJustica
                                                                    ? COR_SEGREDO_JUSTICA
                                                                    : ''
                                                            }}
                                                        >
                                                            <Typography>
                                                                {this.retornoVistaRegimentalToString(registro)}
                                                            </Typography>
                                                        </TableCell>
                                                    )}
                                                    {this.existeProcessoComRevisor && (
                                                        <TableCell
                                                            style={{
                                                                textAlign: 'center',
                                                                color: registro.segredoJustica
                                                                    ? COR_SEGREDO_JUSTICA
                                                                    : ''
                                                            }}
                                                        >
                                                            <Typography>{this.revisorToString(registro)}</Typography>
                                                        </TableCell>
                                                    )}
                                                    <TableCell
                                                        style={{
                                                            textAlign: 'center',
                                                            color: registro.segredoJustica ? COR_SEGREDO_JUSTICA : ''
                                                        }}
                                                    >
                                                        <Typography>{this.impedimentosToString(registro)}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        );
                                    })
                                ) : (
                                    <Typography sx={{ mt: 4, mb: 4, color: 'red' }}>
                                        Não existem preferências cadastradas.
                                    </Typography>
                                )}
                            </TableBody>
                        </Table>
                    </NewWindow>
                )}
            </>
        );
    }
}

export default inject('stores')(observer(ModalListaPreferencias));
